import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, Opp, Result, SeasonStats, StandingsTable, gameDate } from "./OverviewTab";

const face = (p) => (
  <span className="tm-face"><img src={p.photo} alt="" loading="lazy" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} /></span>
);

export function ScheduleTab({ page }) {
  const [filter, setFilter] = useState("all");
  const rows = page.schedule.filter((g) => filter === "all" || (filter === "results" ? g.state === "post" : g.state !== "post"));
  let w = 0; let l = 0;
  const record = Object.fromEntries(page.schedule.filter((g) => g.state === "post").map((g) => { if (g.win) w += 1; else l += 1; return [g.id, `${w}-${l}`]; }));
  return (
    <Card title="Season schedule" id="schedule">
      <div className="tm-pills">
        {[["all", "All games"], ["upcoming", "Upcoming"], ["results", "Results"]].map(([k, label]) => (
          <button key={k} type="button" className={filter === k ? "is-active" : ""} onClick={() => setFilter(k)} data-testid={`schedule-filter-${k}`}>{label}</button>
        ))}
      </div>
      <table className="tm-table" data-testid="team-schedule">
        <thead><tr><th>Date</th><th>Opponent</th><th>Result / time</th><th>W-L</th><th>Venue</th></tr></thead>
        <tbody>
          {rows.map((g) => (
            <tr key={g.id}><td>{gameDate(g.date)}</td><td><Opp g={g} /></td><td><Result g={g} /></td><td>{record[g.id] || "—"}</td><td>{g.home ? "Home" : "Away"}{g.venue ? ` · ${g.venue}` : ""}</td></tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function RosterTab({ page }) {
  const players = [...page.roster].sort((a, b) => Number(a.jersey ?? 999) - Number(b.jersey ?? 999));
  return (
    <Card title={`Roster · ${players.length} players`} id="roster">
      <table className="tm-table" data-testid="team-roster">
        <thead><tr><th>#</th><th>Player</th><th>Pos</th><th>Age</th><th>Height</th><th>Weight</th><th>Exp</th><th>College</th></tr></thead>
        <tbody>
          {players.map((p) => (
            <tr key={p.id}>
              <td className="tm-num">{p.jersey ?? "—"}</td>
              <td><Link className="tm-player" to={`/player/${p.id}`}>{face(p)}{p.name}{p.injury && <em className="tm-inj">{p.injury}</em>}</Link></td>
              <td>{p.position}</td><td>{p.age ?? "—"}</td><td>{p.height || "—"}</td><td>{p.weight || "—"}</td>
              <td>{p.experience === 0 ? "R" : p.experience ?? "—"}</td><td>{p.college || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

export function StatsTab({ page }) {
  return (
    <div className="tm-grid">
      <Card title="Team season stats · league rank" id="team-stats" className="tm-span-4"><SeasonStats rows={page.season_stats} /></Card>
      <Card title="Player averages" id="player-stats" className="tm-span-8">
        <table className="tm-table" data-testid="team-player-stats">
          <thead><tr><th>Player</th><th>GP</th><th>MIN</th><th>PTS</th><th>REB</th><th>AST</th><th>3PM</th><th>STL</th><th>BLK</th><th>FG%</th></tr></thead>
          <tbody>
            {page.player_stats.map((p) => (
              <tr key={p.id}>
                <td><Link className="tm-player" to={`/player/${p.id}`}>{face(p)}{p.name}</Link></td>
                <td>{p.gp}</td><td>{p.min}</td><td className="tm-hi">{p.pts}</td><td>{p.reb}</td><td>{p.ast}</td><td>{p.fg3m}</td><td>{p.stl}</td><td>{p.blk}</td><td>{p.fg_pct ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {!page.player_stats.length && <p className="tm-empty">No box scores yet this season.</p>}
      </Card>
      <Card title={`${page.standing?.conference || "Conference"} standings`} id="conference" className="tm-span-12">
        <StandingsTable rows={page.conference} highlight={page.team_id} id="conference" />
      </Card>
    </div>
  );
}

export function DepthTab({ page }) {
  return (
    <Card title="Depth chart · ordered by minutes per game" id="depth">
      <div className="tm-depth" data-testid="team-depth">
        {page.depth.map((g) => (
          <div key={g.position} className="tm-depth-col">
            <h4>{g.label}</h4>
            {g.players.map((p, i) => (
              <Link key={p.id} to={`/player/${p.id}`} className={`tm-depth-row ${i < (g.position === "C" ? 1 : 2) ? "is-starter" : ""}`}>
                <span className="tm-num">{p.jersey ?? "—"}</span>{face(p)}<span className="tm-depth-name">{p.name}{p.injury && <em className="tm-inj">{p.injury}</em>}</span>
                <small>{p.mpg != null ? `${p.mpg} MPG` : "—"}</small>
              </Link>
            ))}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function InjuriesTab({ page }) {
  return (
    <Card title="Injury report" id="injuries">
      {page.injuries.length ? page.injuries.map((p) => (
        <div key={p.id} className="tm-list-row" data-testid={`team-injury-${p.id}`}>
          {face(p)}<b>{p.name}</b><span>{p.position}</span>
          <em className={`tm-inj ${p.injury === "Out" ? "is-out" : ""}`}>{p.injury}</em>
          <small>{p.injury_date ? `since ${gameDate(p.injury_date, { month: "short", day: "numeric" })}` : ""}</small>
        </div>
      )) : <p className="tm-empty">No injuries reported.</p>}
    </Card>
  );
}

export function TransactionsTab({ page }) {
  return (
    <Card title="Recent transactions" id="transactions">
      {page.transactions.length ? page.transactions.map((t, i) => (
        <div key={i} className="tm-list-row" data-testid={`team-transaction-${i}`}>
          <small className="tm-date">{gameDate(t.date, { month: "short", day: "numeric" })}</small><span>{t.description}</span>
        </div>
      )) : <p className="tm-empty">No recent transactions.</p>}
    </Card>
  );
}

export function NewsTab({ page }) {
  return (
    <Card title="Latest news" id="news-all">
      <div className="tm-news">
        {page.news.map((n, i) => (
          <a key={i} className="tm-news-card" href={n.link || undefined} target="_blank" rel="noreferrer" data-testid={`team-news-${i}`}>
            {n.image && <img src={n.image} alt="" loading="lazy" />}
            <div><b>{n.headline}</b><p>{n.description}</p><small>{n.published ? gameDate(n.published, { month: "short", day: "numeric" }) : ""}{n.type ? ` · ${n.type}` : ""}</small></div>
          </a>
        ))}
      </div>
      {!page.news.length && <p className="tm-empty">No news right now.</p>}
    </Card>
  );
}
